import { useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Loading } from './components';
import ThemeConfig from './theme';
import Router from './routes';
import NavBar from './components/NavBar/NavBar';
import ErrorHandlerModal from './components/ErrorHandlerModal/ErrorHandlerModal';
import { get, isEmpty, isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getSessionDetails } from './utils/localStorage';
import { setSessionDetails, verifySession } from './actions/session';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useIdleTimer } from 'react-idle-timer';
import {
  IDLE_TIMEOUT_MESSAGE,
  IDLE_TIMER_SYNC_TIMERS,
  IDLE_TIMER_THROTTLE,
  IDLE_TIMER_TIMEOUT,
  TOKEN_CHECK_TIME_INTERVAL,
  TOKEN_UPDATE_TIME_WINDOW,
} from './constants/constants';
import 'react-toastify/dist/ReactToastify.css';
import CommonFooter from 'components/CommonFooter';
import { isTokenExpired } from 'constants/tokenValidity';
import { datadogRum } from '@datadog/browser-rum';
import useLocalStorageListener from 'helpers/useLocalStorageListener';
import { getVendorOnboardingStage } from 'actions/vendorOnboarding';
import corlFavIcon from 'assets/images/corl-logo-loading.png';
import { Helmet } from 'react-helmet';
import { datadogLogs } from '@datadog/browser-logs';
import { ACCESS_TOKEN, SESSION_DETAILS, ISAUTHENTICATED, CORL_TOKEN, SESSION_IN_PROGRESS } from './utils/localStorage';
import { useGet } from 'hooks';

const SYSTEM_NAME_LAST_STEP = 'WHAT_NEXT_V2';

const App = () => {
  const { isLoading, isAuthenticated, getAccessTokenSilently, logout } = useAuth0();
  const dispatch = useDispatch();
  let history = useHistory();

  const [showNavbar, setShowNavbar] = useState(true);
  const details = useSelector((state) => state.session.details);
  const hasAcceptedTerms = useSelector((state) => state.session.hasAcceptedTerms);
  const vendorOnboardingStage = useSelector((state) => state.vendorOnboarding.stage);

  const isVendorOnboardingWizardCompleted = useSelector((state) => state.session.isVendorOnboardingWizardCompleted);
  const isUserCreatedAfterVendorOnboardingCompleted = useSelector((state) => state.session.isUserCreatedAfterVendorOnboardingCompleted);

  const flowFetcher = useGet('/vendor-onboarding/flow', {}, { enabled: !hasAcceptedTerms });
  const flows = useMemo(() => {
    const wizards = get(flowFetcher.data, 'Data', []);
    if (!Array.isArray(wizards)) return [];
    return wizards;
  }, [flowFetcher.data]);
  const lastOnboardingStep = flows.find((flow) => flow.SystemName === SYSTEM_NAME_LAST_STEP);
  const currentStepInNewFlow = flows.find((flow) => `${flow.WizardId}` === `${vendorOnboardingStage?.WizardId}`);

  const [isTokenExpire, setIsTokenExpire] = useState(false);

  const handleLogout = () => {
    dispatch({ type: 'RESET_APP_STATE' });
    window.localStorage.removeItem(SESSION_DETAILS);
    window.localStorage.removeItem(ACCESS_TOKEN);
    window.localStorage.removeItem(ISAUTHENTICATED);
    window.localStorage.removeItem(CORL_TOKEN);
    window.localStorage.removeItem(SESSION_IN_PROGRESS);
    logout({
      returnTo: window.location.origin + '/logout',
    });
  };

  const onIdle = () => {
    history.push({
      pathname: '/error',
      state: {
        idleErrorMessage: IDLE_TIMEOUT_MESSAGE,
        logoutUser: true,
      },
    });
  };

  useEffect(() => {
    if (
      !hasAcceptedTerms &&
      isVendorOnboardingWizardCompleted !== undefined &&
      isUserCreatedAfterVendorOnboardingCompleted !== undefined &&
      lastOnboardingStep?.WizardId
    ) {
      if (!isEmpty(vendorOnboardingStage) && currentStepInNewFlow) {
        // resume inprogress onboarding flow
        history.push(`/vendor-onboarding-wizard?tab=${vendorOnboardingStage?.WizardId}`);
      } else if (isVendorOnboardingWizardCompleted && isUserCreatedAfterVendorOnboardingCompleted) {
        // if user is created after vendor already completed onboarding, redirect them to last step of onboarding flow (what next page)
        history.push(`/vendor-onboarding-wizard?tab=${lastOnboardingStep.WizardId}`);
      } else {
        history.push(`/vendor-onboarding-wizard`);
      }
    }
  }, [
    hasAcceptedTerms,
    vendorOnboardingStage,
    currentStepInNewFlow,
    isVendorOnboardingWizardCompleted,
    isUserCreatedAfterVendorOnboardingCompleted,
    lastOnboardingStep,
    history,
  ]);

  useEffect(() => {
    if (!isNil(details?.UserId)) {
      dispatch(getVendorOnboardingStage(details?.UserId));
    }
  }, [details]);

  const IdleTimer = useIdleTimer({
    onIdle,
    timeout: IDLE_TIMER_TIMEOUT,
    throttle: IDLE_TIMER_THROTTLE,
    crossTab: true,
    leaderElection: true,
    syncTimers: IDLE_TIMER_SYNC_TIMERS,
  });

  axios.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem('corl-token') || '';
      let isExpired = isTokenExpired(token);

      if (isExpired) {
        setIsTokenExpire(true);
        if (
          config?.url === '/app/usersession' &&
          (config?.method === 'post' || config?.method === 'get') &&
          window.localStorage.getItem('sessionInProgress') !== 'yes'
        ) {
          // Keep the old flow for reference
          // const data = {
          //   appName: process.env.REACT_APP_NAME,
          // };
          // await dispatch(
          //   getSession(data, getAccessTokenSilently, null, () => {
          //     history.push({
          //       pathname: '/unauthorized',
          //       state: {
          //         text: 'Your session is expired. You have been logged out. Please login again.',
          //       },
          //     });
          //   })
          // );
          setShowNavbar(true);
          handleLogout();
        } else if (config?.url === '/app/usersession' && (config?.method === 'post' || config?.method === 'get')) {
          return config;
        } else if (window.localStorage.getItem('sessionInProgress') === 'yes') {
          throw new axios.Cancel('cancel');
        } else {
          // Keep the old flow for reference
          // const data = {
          //   appName: process.env.REACT_APP_NAME,
          // };
          // await dispatch(
          //   getSession(data, getAccessTokenSilently, null, () => {
          //     history.push({
          //       pathname: '/unauthorized',
          //       state: {
          //         text: 'Your session is expired. You have been logged out. Please login again.',
          //       },
          //     });
          //   })
          // );
          setShowNavbar(true);
          handleLogout();
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(undefined, function (error) {
    let errors = error?.response?.data?.Errors;

    // Handle error response for both new and old APIs
    let errorList = errors?.map((err) => err?.Message) || [error?.response?.data?.Message];

    if (error.response?.status === 401) {
      setShowNavbar(false);
      // Keep the old flow for reference
      // const data = {
      //   appName: process.env.REACT_APP_NAME,
      // };
      // await dispatch(
      //   getSession(data, getAccessTokenSilently, null, () => {
      //     history.push({
      //       pathname: '/unauthorized',
      //       state: {
      //         text: 'Your session is expired. You have been logged out. Please login again.',
      //       },
      //     });
      //   })
      // );
      handleLogout();
    } else if (error.response?.status === 500) {
      if (errorList?.length > 0) {
        const item = errorList?.find((it) => it.toLowerCase().includes('session has expired') || it.toLowerCase().includes('unauthorized access'));
        if (item) {
          localStorage.removeItem('corl-token');
          setShowNavbar(true);
          history.push({
            pathname: '/error',
            state: {
              errors: errorList,
              logoutUser: true,
            },
          });
        }
      } else {
        setShowNavbar(true);
        history.push({
          pathname: '/error',
          state: {
            errors: errorList,
            logoutUser: false,
          },
        });
      }
    }
    return Promise.reject(error);
  });

  useEffect(() => {
    if (isNil(localStorage.getItem('corl-token'))) {
      setShowNavbar(false);
    } else {
      setShowNavbar(true);
      dispatch(verifySession());
    }
  }, [details, dispatch]);

  useEffect(() => {
    const sessionDetails = getSessionDetails();
    dispatch(setSessionDetails(sessionDetails));
  }, [dispatch]);

  useEffect(() => {
    const envName = process.env.REACT_APP_ENVIRONMENT;
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    const datadogSessionReplaySampleRate = parseInt(process.env.REACT_APP_SESSION_REPLAY_SAMPLE_RATE) || 0;
    const monitorProxyEndpoint = process.env.REACT_APP_MONITOR_PROXY_ENDPOINT;
    const version = process.env.REACT_APP_VERSION;

    datadogRum.init({
      applicationId: 'e18584ac-f3fe-489a-9e15-e5661809bb5f',
      clientToken: 'pubabcd0f5d88c51d868dd9616bc0744a53',
      site: 'datadoghq.com',
      service: 'corl-vendor-portal',
      env: envName,
      // Specify a version number to identify the deployed version of your application in Datadog
      version: version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: datadogSessionReplaySampleRate,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [apiEndpoint],
      forwardErrorsToLogs: true,
      proxy: monitorProxyEndpoint,
    });
    if (envName !== 'DEVELOPMENT') {
      datadogLogs.init({
        clientToken: 'pubabcd0f5d88c51d868dd9616bc0744a53',
        applicationId: 'e18584ac-f3fe-489a-9e15-e5661809bb5f',
        site: 'datadoghq.com',
        service: 'corl-vendor-portal',
        sessionSampleRate: 100,
        version: version,
        proxy: monitorProxyEndpoint,
        env: envName,
        allowedTracingUrls: [apiEndpoint],
        forwardErrorsToLogs: true,
      });
      const sessionDetails = getSessionDetails();
      if (sessionDetails) {
        datadogLogs.setUserProperty('session', sessionDetails);
      }
    }

    const interval = setInterval(() => {
      //check the token expiry beforehand
      //if the token will be expiring in TOKEN_UPDATE_TIME_WINDOW,
      //Obtain a new token and create a new session
      const token = localStorage.getItem('corl-token') || '';
      const willBeExpired = isTokenExpired(token, TOKEN_UPDATE_TIME_WINDOW);

      if (willBeExpired) {
        // Keep the old flow for reference
        // const data = {
        //   appName: process.env.REACT_APP_NAME,
        // };
        // await dispatch(
        //   getSession(data, getAccessTokenSilently, null, () => {
        //     history.push({
        //       pathname: '/unauthorized',
        //       state: {
        //         text: 'Your session is expired. You have been logged out. Please login again.',
        //       },
        //     });
        //   })
        // );
        handleLogout();
      }
    }, TOKEN_CHECK_TIME_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, []);

  //Setup eventlistener for change in localstorgae value
  useLocalStorageListener('CORL_sessionDetails');

  if (isLoading) {
    return <Loading />;
  }
  return (
    <ThemeConfig>
      {isTokenExpire ? (
        <Loading />
      ) : (
        <div id="app" className="d-flex flex-column h-100">
          {showNavbar && <NavBar />}
          <div className="flex-grow-1 app-main-content">
            <Router />
          </div>
          {showNavbar && <CommonFooter />}
        </div>
      )}
      {/* {!hasAcceptedTerms && <TermsAndConditions />} */}
      <ErrorHandlerModal />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        closeButton={false}
        theme="light"
        bodyClassName="toastBody"
      />
      <Helmet>
        <link rel="shortcut icon" href={corlFavIcon} />
      </Helmet>
    </ThemeConfig>
  );
};

export default App;
